import {
  createDisposable,
  Disposable,
  disposedDisposable,
} from "./Disposable";

export interface CommandContract<Input = void, Output = void> {
  input: Input;
  output: Output;
}

export type CommandMap = object;
export type CommandName<Commands extends CommandMap> = Extract<
  keyof Commands,
  string
>;

type InputOf<Contract> =
  Contract extends CommandContract<infer Input, unknown> ? Input : never;
type OutputOf<Contract> =
  Contract extends CommandContract<unknown, infer Output> ? Output : never;

export interface CommandContext {
  readonly signal: AbortSignal;
}

export type CommandHandler<Contract> = (
  input: InputOf<Contract>,
  context: CommandContext,
) => OutputOf<Contract> | Promise<OutputOf<Contract>>;

export interface CommandRegistrationOptions {
  signal?: AbortSignal;
}

export interface CommandExecutionOptions {
  signal?: AbortSignal;
}

type CommandArguments<Contract> = [InputOf<Contract>] extends [void]
  ? [input?: InputOf<Contract>, options?: CommandExecutionOptions]
  : [input: InputOf<Contract>, options?: CommandExecutionOptions];

type RegisteredHandler = {
  handler: CommandHandler<CommandContract<unknown, unknown>>;
  registration: Disposable;
};

export class CommandRegistry<Commands extends CommandMap>
  implements Disposable
{
  private readonly handlers = new Map<CommandName<Commands>, RegisteredHandler>();
  private readonly activeExecutions = new Set<AbortController>();
  private _disposed = false;

  get disposed(): boolean {
    return this._disposed;
  }

  register<Name extends CommandName<Commands>>(
    name: Name,
    handler: CommandHandler<Commands[Name]>,
    options: CommandRegistrationOptions = {},
  ): Disposable {
    this.assertActive();
    if (options.signal?.aborted) return disposedDisposable();
    if (this.handlers.has(name)) {
      throw new Error(`Command "${name}" is already registered`);
    }

    let abortHandler: (() => void) | undefined;
    const registration = createDisposable(() => {
      const current = this.handlers.get(name);
      if (current?.registration === registration) this.handlers.delete(name);
      if (abortHandler && options.signal) {
        options.signal.removeEventListener("abort", abortHandler);
      }
    });
    this.handlers.set(name, {
      handler: handler as CommandHandler<CommandContract<unknown, unknown>>,
      registration,
    });

    if (options.signal) {
      abortHandler = () => registration.dispose();
      options.signal.addEventListener("abort", abortHandler, { once: true });
    }
    return registration;
  }

  has<Name extends CommandName<Commands>>(name: Name): boolean {
    return this.handlers.has(name);
  }

  async execute<Name extends CommandName<Commands>>(
    name: Name,
    ...args: CommandArguments<Commands[Name]>
  ): Promise<OutputOf<Commands[Name]>> {
    this.assertActive();
    const registered = this.handlers.get(name);
    if (!registered) throw new Error(`Command "${name}" is not registered`);

    const input = args[0] as InputOf<Commands[Name]>;
    const options = args[1] as CommandExecutionOptions | undefined;
    const controller = new AbortController();
    const forwardAbort = () => controller.abort(options?.signal?.reason);

    if (options?.signal?.aborted) {
      forwardAbort();
    } else {
      options?.signal?.addEventListener("abort", forwardAbort, { once: true });
    }
    this.activeExecutions.add(controller);

    try {
      controller.signal.throwIfAborted();
      const output = await registered.handler(input, {
        signal: controller.signal,
      });
      controller.signal.throwIfAborted();
      return output as OutputOf<Commands[Name]>;
    } finally {
      this.activeExecutions.delete(controller);
      options?.signal?.removeEventListener("abort", forwardAbort);
    }
  }

  unregister<Name extends CommandName<Commands>>(name: Name): void {
    this.handlers.get(name)?.registration.dispose();
  }

  clear(): void {
    for (const { registration } of [...this.handlers.values()]) {
      registration.dispose();
    }
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    for (const controller of this.activeExecutions) {
      controller.abort(new Error("CommandRegistry has been disposed"));
    }
    this.activeExecutions.clear();
    this.clear();
  }

  private assertActive(): void {
    if (this._disposed) throw new Error("CommandRegistry has been disposed");
  }
}
